import type {
  ProductBackorders,
  ProductCondition,
  ProductDetail,
  ProductInput,
  ProductTexts,
  ProductType,
  ProductUnit,
  ProductVisibility,
} from '../api/business/types';
import type { MessageKey } from '../i18n/sk';

import {
  endOfDayIso,
  moneyInputValue,
  parseMoneyInput,
  parseOptionalWholeNumber,
  parseUnitQuantity,
  parseWholeNumber,
  startOfDayIso,
  toDateInputValue,
  unitQuantityInputValue,
} from './parse';

/** A product's texts in one language as the form edits them, '' where there is none. */
export type ProductTextsDraft = Record<keyof ProductTexts, string>;

/** A product as its form edits it (R0030 WP-05, WP-07): numbers and dates as typed, texts per content language. */
export type ProductDraft = {
  texts: Record<string, ProductTextsDraft>;
  /** R0030 WP-09. */
  type: ProductType;
  ean: string;
  sku: string;
  mpn: string;
  /** With VAT. */
  price: string;
  vatRate: number;
  salePrice: string;
  /** `YYYY-MM-DD`, '' — no bound. */
  saleFrom: string;
  saleTo: string;
  costPrice: string;
  weightGrams: string;
  widthMm: string;
  heightMm: string;
  depthMm: string;
  visibility: ProductVisibility;
  featured: boolean;
  backorders: ProductBackorders;
  lowStockThreshold: string;
  minOrderQuantity: string;
  condition: ProductCondition;
  showCondition: boolean;
  /** '' — no unit price. */
  unitLabel: ProductUnit | '';
  unitQuantity: string;
  /** With VAT, as typed; '' is none. */
  ecotax: string;
  /** `YYYY-MM-DD`, '' — no date. */
  availableFrom: string;
  availableForOrder: boolean;
  showPrice: boolean;
  onlineOnly: boolean;
  onSaleBadge: boolean;
};

/** Longer than a search engine shows: the form warns, the API still takes it up to the hard limit. */
export const META_TITLE_ADVISED = 60;
export const META_TITLE_MAX = 70;
export const META_DESCRIPTION_ADVISED = 155;
export const META_DESCRIPTION_MAX = 160;
export const AVAILABILITY_TEXT_MAX = 255;

/** What the form sends, or the message of what it cannot accept. */
export type ProductDraftResult =
  { input: ProductInput; problem: null } | { input: null; problem: MessageKey };

const TEXT_FIELDS: Array<keyof ProductTexts> = [
  'name',
  'shortDescription',
  'description',
  'slug',
  'availableNow',
  'availableLater',
  'metaTitle',
  'metaDescription',
];

const numberText = (value: number | null): string => (value === null ? '' : String(value));

/** Changes one property of the draft. */
export type DraftSetter = <TKey extends keyof ProductDraft>(key: TKey, value: ProductDraft[TKey]) => void;

/** Changes one text of the draft in one language. */
export type DraftTextSetter = (code: string, field: keyof ProductTexts, value: string) => void;

/** A language the form has no texts for yet. */
export const emptyTexts = (): ProductTextsDraft =>
  Object.fromEntries(TEXT_FIELDS.map((field) => [field, ''])) as ProductTextsDraft;

/** The form of a product being edited, or of a new one. */
export const draftOf = (product: ProductDetail | null, languages: Array<string>): ProductDraft => ({
  texts: Object.fromEntries(
    languages.map((code) => [
      code,
      Object.fromEntries(
        TEXT_FIELDS.map((field) => [field, product?.translations[code]?.[field] ?? '']),
      ) as ProductTextsDraft,
    ]),
  ),
  type: product?.type ?? 'STANDARD',
  ean: product?.ean ?? '',
  sku: product?.sku ?? '',
  mpn: product?.mpn ?? '',
  price: moneyInputValue(product?.defaultPriceMinor ?? null),
  vatRate: product?.vatRateBasisPoints ?? 2300,
  salePrice: moneyInputValue(product?.salePriceMinor ?? null),
  saleFrom: toDateInputValue(product?.saleFrom ?? null),
  saleTo: toDateInputValue(product?.saleTo ?? null),
  costPrice: moneyInputValue(product?.costPriceMinor ?? null),
  weightGrams: numberText(product?.weightGrams ?? null),
  widthMm: numberText(product?.widthMm ?? null),
  heightMm: numberText(product?.heightMm ?? null),
  depthMm: numberText(product?.depthMm ?? null),
  visibility: product?.visibility ?? 'EVERYWHERE',
  featured: product?.featured ?? false,
  backorders: product?.backorders ?? 'DENY',
  lowStockThreshold: numberText(product?.lowStockThreshold ?? null),
  minOrderQuantity: String(product?.minOrderQuantity ?? 1),
  condition: product?.condition ?? 'NEW',
  showCondition: product?.showCondition ?? false,
  unitLabel: product?.unitLabel ?? '',
  unitQuantity: unitQuantityInputValue(product?.unitQuantity ?? null),
  ecotax: product && product.ecotaxMinor > 0 ? moneyInputValue(product.ecotaxMinor) : '',
  availableFrom: product?.availableFrom ?? '',
  availableForOrder: product?.availableForOrder ?? true,
  showPrice: product?.showPrice ?? true,
  onlineOnly: product?.onlineOnly ?? false,
  onSaleBadge: product?.onSaleBadge ?? false,
});

/**
 * The texts a write sends: on a create every text written, on a change only what changed — an emptied text as
 * null, which clears it (an emptied slug is made again from the name).
 */
export const textChangesOf = (
  draft: ProductDraft,
  original: ProductDraft | null,
  languages: Array<string>,
): Record<string, Partial<ProductTexts>> => {
  const changes: Record<string, Partial<ProductTexts>> = {};

  for (const code of languages) {
    const entry: Partial<ProductTexts> = {};

    for (const field of TEXT_FIELDS) {
      const text = (draft.texts[code]?.[field] ?? '').trim();

      if (original === null) {
        if (text !== '') entry[field] = text;
      } else if (text !== (original.texts[code]?.[field] ?? '').trim()) {
        entry[field] = text === '' ? null : text;
      }
    }

    if (Object.keys(entry).length > 0) {
      changes[code] = entry;
    }
  }

  return changes;
};

/**
 * The product write the form makes, checked as far as the form can: money and whole numbers typed right, the
 * default language's name there, the sale below the price and its window in order; a unit price with both its
 * unit and quantity, the eco-tax within the price, a hidden price only on a product that cannot be ordered, the
 * search-engine texts within their limits. The API checks it all again.
 */
export const inputFrom = (
  draft: ProductDraft,
  original: ProductDraft | null,
  languages: Array<string>,
): ProductDraftResult => {
  const price = parseMoneyInput(draft.price);
  const salePrice = parseMoneyInput(draft.salePrice);
  const costPrice = parseMoneyInput(draft.costPrice);
  const measures = [
    draft.weightGrams,
    draft.widthMm,
    draft.heightMm,
    draft.depthMm,
    draft.lowStockThreshold,
  ].map(parseOptionalWholeNumber);
  const minOrderQuantity = parseWholeNumber(draft.minOrderQuantity);
  const unitQuantity = parseUnitQuantity(draft.unitQuantity);
  const ecotax = parseMoneyInput(draft.ecotax);

  if (price === undefined || salePrice === undefined || costPrice === undefined || ecotax === undefined) {
    return { input: null, problem: 'money.invalid' };
  }

  if ((draft.texts[languages[0]]?.name ?? '').trim() === '') {
    return { input: null, problem: 'languages.defaultRequired' };
  }

  if (measures.some((value) => value === undefined)) {
    return { input: null, problem: 'products.wholeNumber' };
  }

  if (minOrderQuantity === undefined || minOrderQuantity < 1) {
    return { input: null, problem: 'products.minQuantityInvalid' };
  }

  if (salePrice === null && (draft.saleFrom !== '' || draft.saleTo !== '')) {
    return { input: null, problem: 'products.saleWithoutPrice' };
  }

  if (salePrice !== null && (price === null || salePrice >= price)) {
    return { input: null, problem: 'products.saleNotBelow' };
  }

  if (draft.saleFrom !== '' && draft.saleTo !== '' && draft.saleFrom > draft.saleTo) {
    return { input: null, problem: 'products.saleWindowReversed' };
  }

  if (unitQuantity === undefined) {
    return { input: null, problem: 'legal.unitQuantityInvalid' };
  }

  if ((draft.unitLabel === '') !== (unitQuantity === null)) {
    return { input: null, problem: 'legal.unitIncomplete' };
  }

  if (ecotax !== null && price !== null && ecotax > price) {
    return { input: null, problem: 'legal.ecotaxAbovePrice' };
  }

  if (!draft.showPrice && draft.availableForOrder) {
    return { input: null, problem: 'availability.priceHiddenOrderable' };
  }

  const tooLong = (field: keyof ProductTexts, maximum: number): boolean =>
    languages.some((code) => (draft.texts[code]?.[field] ?? '').trim().length > maximum);

  if (tooLong('metaTitle', META_TITLE_MAX) || tooLong('metaDescription', META_DESCRIPTION_MAX)) {
    return { input: null, problem: 'seo.tooLong' };
  }

  const [weightGrams, widthMm, heightMm, depthMm, lowStockThreshold] = measures as Array<number | null>;
  const creating = original === null;
  const text = (value: string): string | null | undefined =>
    value.trim() === '' ? (creating ? undefined : null) : value.trim();

  return {
    problem: null,
    input: {
      translations: textChangesOf(draft, original, languages),
      ean: text(draft.ean),
      sku: text(draft.sku),
      mpn: text(draft.mpn),
      defaultPriceMinor: price,
      vatRateBasisPoints: draft.vatRate,
      salePriceMinor: salePrice,
      saleFrom: draft.saleFrom === '' ? null : startOfDayIso(draft.saleFrom),
      saleTo: draft.saleTo === '' ? null : endOfDayIso(draft.saleTo),
      costPriceMinor: costPrice,
      weightGrams,
      widthMm,
      heightMm,
      depthMm,
      visibility: draft.visibility,
      featured: draft.featured,
      backorders: draft.backorders,
      lowStockThreshold,
      minOrderQuantity,
      condition: draft.condition,
      showCondition: draft.showCondition,
      unitLabel: draft.unitLabel === '' ? null : draft.unitLabel,
      unitQuantity,
      ecotaxMinor: ecotax ?? 0,
      availableFrom: draft.availableFrom === '' ? null : draft.availableFrom,
      availableForOrder: draft.availableForOrder,
      showPrice: draft.showPrice,
      onlineOnly: draft.onlineOnly,
      onSaleBadge: draft.onSaleBadge,
      // Only when it is not what the product already is — a create left out is STANDARD (R0030 WP-09).
      ...(draft.type !== (original?.type ?? 'STANDARD') ? { type: draft.type } : {}),
    },
  };
};
